import type { PlatformRequestConfig } from 'Models/uiConfig/requestConfig';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import isTruthy from 'Utils/isTruthy';

import { bigcommerceAppClientId } from '../../../Stores/_common/constants';
import { getCountry, getCustomerGroupId } from './helpers';

const customer = getCustomerAsync();

// BigCommerce only contributes string fields ("country", "customerGroupId") so
// we pick a string-only slice of the canonical `RequestExtra` map.
// Stays compatible with the broader `RequestExtra` consumed by the request pipeline.
type BigcommerceExtra = Record<string, string> & RequestExtra;

interface BigCommerceSearchRequestDefaults {
  extra: BigcommerceExtra;
  referrer?: string;
}

// Shape of the `customer` payload embedded in BigCommerce's
// `/customer/current.jwt` response. Only the fields we currently consume are
// declared; the JWT carries more (email, name, etc.) but they're not used here.
interface BigCommerceCustomer {
  id?: number;
  group_id?: number;
}

interface BigCommerceCustomerJwt {
  token: string;
}

/**
 * Validate the `customer` payload that BigCommerce embeds in the JWT.
 * `id` and `group_id` arrive as numbers in well-formed payloads but
 * we have observed merchant proxies that re-encode them as strings, so
 * we narrow each field individually before exposing it to the rest of
 * the pipeline (which uses `===` comparisons against numbers).
 */
function extractBigCommerceCustomer(payload: unknown): BigCommerceCustomer | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const { customer } = payload as { customer?: unknown };
  if (!customer || typeof customer !== 'object') {
    return undefined;
  }
  const result: BigCommerceCustomer = {};
  const { id, group_id: groupId } = customer as { id?: unknown; group_id?: unknown };
  const groupIdKey = 'group_id';
  if (typeof id === 'number' && Number.isFinite(id)) {
    result.id = id;
  } else if (typeof id === 'string' && id !== '') {
    const parsed = Number(id);
    if (Number.isFinite(parsed)) {
      result.id = parsed;
    }
  }
  if (typeof groupId === 'number' && Number.isFinite(groupId)) {
    result[groupIdKey] = groupId;
  } else if (typeof groupId === 'string' && groupId !== '') {
    const parsed = Number(groupId);
    if (Number.isFinite(parsed)) {
      result[groupIdKey] = parsed;
    }
  }
  return result.id !== undefined || result.group_id !== undefined ? result : undefined;
}

function collectExtra(): BigcommerceExtra {
  const extra: BigcommerceExtra = {};

  const country = getCountry();
  if (country) {
    extra.country = country.toLowerCase();
  }

  const customerGroupId = getCustomerGroupId();
  if (customerGroupId) {
    extra.customerGroupId = customerGroupId.toLowerCase();
  }

  return extra;
}

function getLocalPreselection(): null {
  return null;
}

Object.assign(getLocalPreselection, { defaults: { getter: getLocalPreselection } });

async function getCustomerAsync(): Promise<BigCommerceCustomer | null> {
  const options = {
    method: 'GET',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
  };

  return fetch(`/customer/current.jwt?app_client_id=${bigcommerceAppClientId}`, options)
    .then((response): Promise<BigCommerceCustomerJwt> => {
      if (response.ok && !response.redirected) {
        return response.json() as Promise<BigCommerceCustomerJwt>;
      }
      return Promise.reject(response);
    })
    .then((response) => {
      const [, jsonPart] = response.token.split('.');
      if (!jsonPart) {
        window.Convermax.isLoggedIn = false;
        return null;
      }
      let payload: unknown;
      try {
        payload = JSON.parse(atob(jsonPart));
      } catch {
        window.Convermax.isLoggedIn = false;
        return null;
      }
      const customer = extractBigCommerceCustomer(payload);
      if (customer) {
        window.Convermax.customerGroupId = customer.group_id != null ? String(customer.group_id) : undefined;
        window.Convermax.customerId = customer.id;
        window.Convermax.isLoggedIn = true;
        return customer;
      }
      window.Convermax.isLoggedIn = false;
      return null;
    })
    .catch((err: { status?: number } | unknown) => {
      if (typeof err === 'object' && err !== null && (err as { status?: number }).status !== 404) {
        console.error(err);
        window.Convermax.isLoggedIn = null;
      } else {
        window.Convermax.isLoggedIn = false;
      }
      return null;
    });
}

function getSearchRequestDefaults(pageType: string): BigCommerceSearchRequestDefaults {
  const requestDefaults: BigCommerceSearchRequestDefaults = {
    extra: collectExtra(),
  };

  if (
    window.location.pathname.split('/').filter(isTruthy).length >= 1 &&
    (pageType === 'category' || pageType === 'brand')
  ) {
    requestDefaults.referrer = encodeURIComponent(window.location.pathname);
  }

  return requestDefaults;
}

function getAutocompleteRequestDefaults(): Record<string, string> {
  return collectExtra();
}

const requestConfig: PlatformRequestConfig = {
  getLocalPreselection,
  getCustomerAsync: (): ReturnType<typeof getCustomerAsync> => customer,
  getSearchRequestDefaults,
  getAutocompleteRequestDefaults,
};

export default requestConfig;
